import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { X, Plus, Loader2, Upload, Camera } from 'lucide-react';
import { useApiMutation, useApi } from '../../hooks/useApi';
import { maintenanceAPI, enhancedAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AddMaintenanceRequestForm = ({ onClose, onSuccess, tenantId = null }) => {
  const [formData, setFormData] = useState({
    tenant_id: tenantId || '',
    property_id: '',
    title: '',
    description: '',
    category: 'general_maintenance',
    priority: 'medium'
  });
  const [selectedImages, setSelectedImages] = useState([]);

  const { data: tenantsData } = useApi(() => enhancedAPI.getTenantsWithProperties());
  const { mutate, loading, error } = useApiMutation();
  const { toast } = useToast();

  const categories = [
    { value: 'plumbing', label: '🚿 Plumbing' },
    { value: 'electrical', label: '⚡ Electrical' },
    { value: 'hvac', label: '❄️ HVAC/Heating' },
    { value: 'appliances', label: '🏠 Appliances' },
    { value: 'pest_control', label: '🐛 Pest Control' },
    { value: 'locks_security', label: '🔒 Locks/Security' },
    { value: 'general_maintenance', label: '🔧 General Maintenance' },
    { value: 'emergency', label: '🚨 Emergency' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', description: 'Can wait a few days' },
    { value: 'medium', label: 'Medium', description: 'Should be addressed within a week' },
    { value: 'high', label: 'High', description: 'Needs immediate attention' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;
    const selectedTenant = tenantsData?.find(t => t.id === tenantId);
    
    if (selectedTenant) {
      setFormData(prev => ({
        ...prev,
        tenant_id: tenantId,
        property_id: selectedTenant.property_id
      }));
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create the maintenance request first
      const requestData = {
        ...formData,
        images: [] // Will be populated after image uploads
      };

      const createdRequest = await mutate(maintenanceAPI.create, requestData);
      
      // Upload images if any
      if (selectedImages.length > 0) {
        for (const imageData of selectedImages) {
          try {
            await mutate(maintenanceAPI.uploadImage, createdRequest.id, imageData.file);
          } catch (imageError) {
            console.error('Failed to upload image:', imageError);
          }
        }
      }

      toast({
        title: "Success!",
        description: `Maintenance request "${createdRequest.title}" has been submitted successfully.`,
      });

      onSuccess?.();
      onClose();

    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to submit maintenance request. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Submit Maintenance Request</CardTitle>
          <Button variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {!tenantId && (
              <div>
                <Label htmlFor="tenant_id">Tenant *</Label>
                <select
                  id="tenant_id"
                  name="tenant_id"
                  value={formData.tenant_id}
                  onChange={handleTenantChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a tenant</option>
                  {tenantsData?.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} - {tenant.propertyName} Unit {tenant.unit_number}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label htmlFor="title">Request Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief description of the issue"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="priority">Priority Level *</Label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label} - {priority.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Detailed Description *</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Please provide a detailed description of the issue, including when it started and any relevant details..."
                rows="4"
                required
              />
            </div>

            <div>
              <Label>Photos (Optional)</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 cursor-pointer"
                >
                  <div className="text-center">
                    <Camera className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to add photos</p>
                    <p className="text-xs text-gray-500">JPG, PNG up to 10MB each</p>
                  </div>
                </label>
              </div>

              {selectedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-4">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={image.preview}
                        alt={image.name}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddMaintenanceRequestForm;